import { createHead } from '@unhead/vue/client'
import { createPinia } from 'pinia'
import { createApp } from 'vue'

// __ENTRY_PATHNAME__ seteado en <script> de index.html para obtener pathname original antes de cargar cualquier módulo ES.
// Necesario para determinar si es autonomos-pymes/tarificador/ porque si no redirige a tarificador/ automáticamente
const initialPathname: string =
  (window as Window & { __ENTRY_PATHNAME__?: string }).__ENTRY_PATHNAME__ ??
  window.location.pathname

import VueCookies from 'vue-cookies'
import App from './App.vue'
import './assets/styles/app.scss'
import i18n from './config/i18n.config'
import vuetify from './config/vuetify.config'
// import { makeServer } from './mocks/server'
import { createGtm } from '@gtm-support/vue-gtm'
import { AppInsightsPlugin, type AppInsightsPluginOptions } from 'vue3-application-insights'
import { enableDevLogging } from './config/insight/tracking-http.insight'
import { initEntryTelemetry } from './config/insight/tracking.insight'
import { useInsightStore } from './modules/core/stores/insightStore'
import OneTrust from './plugins/OneTrust'
import { createDefaultRouter } from './router/index.js'
import { createPymesRouter } from './router/pymes/index.js'

if (import.meta.env.VITE_MOCK_SERVER_ENABLE === 'true') {
  const mockServer = () => import('./mocks/server' as string)
  const { makeServer } = await mockServer()
  makeServer()
}

// Comprobar si estamos en flujo de pymes
const pymesBasePath = '/' + import.meta.env.VITE_SERVER_PYMES_BASE_FOLDER
const isPymesEntry = (initialPathname + '/').startsWith(pymesBasePath)

const router = isPymesEntry ? createPymesRouter() : createDefaultRouter()

const aiOptions: AppInsightsPluginOptions = {
  appInsightsConfig: {
    config: {
      connectionString: import.meta.env.VITE_KEY_INSIGHTS,
      disableExceptionTracking: true,
			disableAjaxTracking: true,
			disableFetchTracking: true,
    }
  },
  router: router,
  appName: 'WASISA',
  trackAppErrors: false,
  trackInitialPageView: false,
  cloudRole: 'frontend',
  cloudRoleInstance: 'vue-app'
  /*onLoaded: (appInsights: ApplicationInsights) => {
    // Custom changes for application insights
  },*/
}

const app = createApp(App)
const head = createHead({
  init: [
    {
      title: 'Seguros Médicos ASISA',
      meta: [
        {
          name: 'description',
          content: 'Asisa Web Tarificador personalizable'
        }
      ]
    }
  ]
})

app.use(createPinia())
app.use(vuetify)
app.use(router)
app.use(head)
app.use(i18n)
app.use(
  createGtm({
    id: import.meta.env.VITE_GTM_CONTAINER_ID,
    enabled: import.meta.env.VITE_GTM_ENABLED === 'true',
    debug: import.meta.env.VITE_GTM_DEBUG_ENABLED === 'true',
    trackOnNextTick: false
  })
)
app.use(VueCookies)
app.use(AppInsightsPlugin, aiOptions)
app.use(OneTrust)

const connectionString = import.meta.env.VITE_KEY_INSIGHTS
if (connectionString) {
  initEntryTelemetry(connectionString, 'WASISA', {
    environment: import.meta.env.VITE_APP_ENV,
    version: import.meta.env.VITE_APP_VERSION
  })

  if (import.meta.env.DEV || ['LOCAL', 'DEVELOPMENT'].includes(import.meta.env.VITE_APP_ENV)) {
    enableDevLogging()
  }
}

app.config.errorHandler = (err: any, instance: any, info: string) => {
  const errorStore = useInsightStore()
  if (isProgrammingError(err)) {
    errorStore.trackException(
      {
        message: err.message,
        stack: err.stack,
        name: instance?.$options?.__name || 'Desconocido'
      },
      1,
      info
    )
    /*console.error({
      message: err.message,
      stack: err.stack,
      name: instance?.$options?.__name || 'Desconocido',
      info
    })*/
  }
  if (['LOCAL', 'DEVELOPMENT'].includes(import.meta.env['VITE_APP_ENV'])) {
    console.error(err)
  }
}

function isProgrammingError(err: any) {
  return (
    err instanceof ReferenceError ||
    err instanceof TypeError ||
    err instanceof SyntaxError ||
    err instanceof RangeError /*||
    err instanceof Error*/
  )
}

app.mount('#app')
